export function isSpreadsheetTableHtml(html: string): boolean {
    if (!/<table[^>]*>/i.test(html)) return false;
    if (/<google-sheets-html-origin/i.test(html)) return true;
    if (/td\.xl\d+/i.test(html)) return true;
    if (/<table[^>]*\sclass=["']ExcelTable/i.test(html)) return true;
    if (/\sdata-sheets-value=/i.test(html)) return true;
    // Excel-only clipboard markers that appear WITHOUT the td.xlNN <style> block
    // (modern Excel, Excel-for-Web, legacy 2003/2007). A single-row copy often
    // omits that block, so detection used to fail and the paste fell to the
    // generic path that collapses a one-row table into a single cell. These
    // markers never occur in Google Docs / Word / generic document HTML, so
    // those keep routing to the generic path (preserving in-cell lists).
    if (/content\s*=\s*["']?Excel\.Sheet/i.test(html)) return true;
    if (/xmlns:x\s*=\s*["']urn:schemas-microsoft-com:office:excel/i.test(html)) return true;
    return false;
}

export function isTableHtml(html: string): boolean {
    return /<table[^>]*>/i.test(html) && /<t[dh][^>]*>/i.test(html);
}

// True when the clipboard HTML is essentially a single table with no other
// meaningful content next to it — the case the spreadsheet shortcut handler
// is built for. Pasting from a previous Slate-based editor (and other "rich
// document" sources) brings headings, paragraphs, AND tables in one blob; for
// those the generic deserialize path is correct. `spreadsheetHtmlToPlateTable`
// would otherwise collapse the entire paste down to just the first table.
export function isPureTableHtml(html: string): boolean {
    if (!isTableHtml(html)) return false;
    if (typeof DOMParser === 'undefined') return false;
    try {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const body = doc?.body;
        if (!body) return false;
        const tables = body.querySelectorAll('table');
        if (tables.length !== 1) return false;
        // Strip the table and see whether any visible text content remains.
        const clone = body.cloneNode(true) as HTMLElement;
        clone.querySelectorAll('table').forEach((t) => t.remove());
        const remaining = (clone.textContent ?? '').replace(/ /g, ' ').trim();
        return remaining.length === 0;
    } catch {
        return false;
    }
}

// Walk a DOM node and produce Plate inline children (text with marks).
function inlineChildren(node: Node): any[] {
    const results: any[] = [];
    for (const child of Array.from(node.childNodes)) {
        if (child.nodeType === Node.TEXT_NODE) {
            const t = child.textContent ?? '';
            if (t) results.push({ text: t });
            continue;
        }
        if (child.nodeType !== Node.ELEMENT_NODE) continue;
        const el = child as HTMLElement;
        const tag = el.tagName;

        if (tag === 'BR') {
            results.push({ text: '\n' });
            continue;
        }

        const nested = inlineChildren(el);
        if (nested.length === 0) continue;

        // Apply marks based on tag.
        const marks: Record<string, boolean | string> = {};
        if (tag === 'B' || tag === 'STRONG') marks.bold = true;
        if (tag === 'I' || tag === 'EM') marks.italic = true;
        if (tag === 'U') marks.underline = true;
        if (tag === 'S' || tag === 'DEL' || tag === 'STRIKE') marks.strikethrough = true;
        if (tag === 'CODE') marks.code = true;

        // Read marks from inline style too (Google Docs / Excel express formatting
        // via `style`, not tags). Mirrors the cell-level fidelity of the generic
        // deserialize path so a styled run inside a spreadsheet cell keeps the
        // same leaf marks it would outside one.
        const style = el.getAttribute('style') || '';
        if (/font-weight:\s*(bold|[7-9]\d\d)/i.test(style)) marks.bold = true;
        if (/font-style:\s*italic/i.test(style)) marks.italic = true;
        if (/text-decoration(?:-line)?:[^;]*underline/i.test(style)) marks.underline = true;
        const color = style.match(/(?:^|;)\s*color:\s*([^;]+)/i)?.[1]?.trim();
        if (color) marks.color = color;
        const fontSize = style.match(/font-size:\s*([^;]+)/i)?.[1]?.trim();
        if (fontSize) marks.fontSize = fontSize;

        if (Object.keys(marks).length > 0) {
            for (const leaf of nested) {
                if (typeof leaf.text === 'string') {
                    results.push({ ...marks, ...leaf });
                } else {
                    results.push(leaf);
                }
            }
        } else {
            results.push(...nested);
        }
    }
    return results.length > 0 ? results : [{ text: '' }];
}

// Read text-align from an element's `align` attribute or inline style.
// Excel emits align="right" / Sheets emits style="text-align:right" on the <td>.
// Returns undefined for the default (left) so we don't stamp redundant align.
function readAlign(el: Element): 'right' | 'center' | 'justify' | undefined {
    const attr = (el.getAttribute('align') || '').toLowerCase();
    const style = (el as HTMLElement).getAttribute?.('style') || '';
    const fromStyle = style.match(/text-align:\s*(left|right|center|justify)/i)?.[1]?.toLowerCase();
    // Inline text-align wins over the legacy align attribute when both are set.
    const value = fromStyle || attr;
    return value === 'right' || value === 'center' || value === 'justify' ? value : undefined;
}

// Convert a cell's inner DOM into Plate paragraph blocks. Cell text-align
// (from the <td>, or an inner <p>/<div> which takes precedence) is carried onto
// the paragraph node's `align` — the same property the toolbar AlignMenu sets —
// so a right-aligned spreadsheet column stays right-aligned after paste.
function cellBlocks(td: Element): any[] {
    const blocks: any[] = [];
    const cellAlign = readAlign(td);

    // If the cell has <p> children, treat each as a separate paragraph.
    const paras = td.querySelectorAll(':scope > p, :scope > div');
    if (paras.length > 0) {
        paras.forEach((p) => {
            const align = readAlign(p) ?? cellAlign;
            blocks.push({ type: 'p', children: inlineChildren(p), ...(align ? { align } : {}) });
        });
    }

    // Fallback: treat entire cell as one paragraph.
    if (blocks.length === 0) {
        blocks.push({ type: 'p', children: inlineChildren(td), ...(cellAlign ? { align: cellAlign } : {}) });
    }

    return blocks;
}

// Parse a px width from a <col> element's style or width attribute.
// Returns 0 when no usable positive width is present.
function colPxWidth(col: Element): number {
    const styleWidth = (col as HTMLElement).style?.width || '';
    const fromStyle = parseInt(styleWidth, 10);
    if (Number.isFinite(fromStyle) && fromStyle > 0) return fromStyle;
    const attrWidth = parseInt(col.getAttribute('width') || '', 10);
    if (Number.isFinite(attrWidth) && attrWidth > 0) return attrWidth;
    return 0;
}

export function spreadsheetHtmlToPlateTable(html: string): any | null {
    if (typeof DOMParser === 'undefined') return null;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const table = doc.querySelector('table');
    if (!table) return null;

    const trs = Array.from(table.querySelectorAll('tr'));
    if (trs.length === 0) return null;

    const rows = trs.map((tr) => {
        const cells = Array.from(tr.querySelectorAll(':scope > td, :scope > th')).map((td) => ({
            type: td.tagName === 'TH' ? 'th' : 'td',
            children: cellBlocks(td),
        }));
        return {
            type: 'tr',
            children: cells.length ? cells : [{ type: 'td', children: [{ type: 'p', children: [{ text: '' }] }] }],
        };
    });

    const node: any = { type: 'table', children: rows };

    // Column count = widest row's cell count.
    let columnCount = 0;
    for (const row of rows) {
        const c = row.children.length;
        if (c > columnCount) columnCount = c;
    }

    const cols = Array.from(table.querySelectorAll('colgroup > col'));
    if (cols.length > 0 && columnCount > 0) {
        const widths = cols.map(colPxWidth);
        if (widths.some((w) => w > 0)) {
            const colSizes = Array.from({ length: columnCount }, (_, i) => widths[i] ?? 0);
            node.colSizes = colSizes;
        }
    }

    return node;
}
